import { BaseEntityModel } from '/@/modules/core/app/models/abstracts/BaseEntityModel';
import { FinancialAccount } from '@/modules/financialaccount/models/FinancialAccount';
import { Country } from '@/modules/predefined/models/Country';
import { Address } from '@/modules/address/models/Address';
import { FlinksType } from '@/modules/financialgateway/constants/FlinksType';
import { reject } from 'lodash';
import { Company } from '@/modules/company/models/Company';
import Lang from '/@/modules/core/app/services/Lang';

export class FinancialInstitution extends BaseEntityModel {
  id?: string;
  flinksId?: string;
  name?: string;
  nameFr?: string;
  countryId?: string;
  companyId?: string;
  logoUrl?: string;
  logoBase64?: string;
  active?: boolean;
  country?: Relationship<Country>;
  financialAccounts?: Relationship<FinancialAccount[]>;
  address?: Relationship<Address>;
  company?: Relationship<Company>;

  constructor(model: Partial<FinancialInstitution> = {}) {
    super(model);

    this.id = model.id;
    this.flinksId = model.flinksId;
    this.name = model.name;
    this.nameFr = model.nameFr;
    this.countryId = model.countryId;
    this.companyId = model.companyId;
    this.logoUrl = model.logoUrl;
    this.logoBase64 = model.logoBase64;
    this.active = model.active;
    // @ts-ignore
    this.country = Country.mapModelOrNull(model.country);
    // @ts-ignore
    this.financialAccounts = FinancialAccount.mapModelsOrNull(model.financialAccounts);
    // @ts-ignore
    this.address = Address.mapModelOrNull(model.address);
    // @ts-ignore
    this.company = Company.mapModelOrNull(model.company);
  }

  get displayName(): string | undefined {
    return Lang.isFrench() && this.nameFr ? this.nameFr : this.name;
  }

  get isFetchingData(): boolean {
    return this.hasPendingAccounts || this.hasRefreshingAccounts;
  }

  get hasPendingAccounts(): boolean {
    return !!this.financialAccounts?.find(
      (financialAccount: FinancialAccount) => financialAccount.isFlinksPending
    );
  }

  get hasAuthorizationErrorAccounts(): boolean {
    return !!this.financialAccounts?.find(
      (financialAccount: FinancialAccount) =>
        financialAccount.financialGatewayToken.isUnauthorizedErrorCode
    );
  }

  get hasRetryLaterErrorAccounts(): boolean {
    return !!this.financialAccounts?.find(
      (financialAccount: FinancialAccount) =>
        financialAccount.financialGatewayToken.isRetryLaterCode
    );
  }

  get hasExpiredAccounts(): boolean {
    return !!this.financialAccounts?.find(
      (financialAccount: FinancialAccount) =>
        financialAccount.financialGatewayToken.isExpiredErrorCode
    );
  }

  get hasRefreshingAccounts(): boolean {
    return !!this.financialAccounts?.find(
      (financialAccount: FinancialAccount) => financialAccount.isRefreshingBalances
    );
  }

  get isFlinksInvestmentInstitution(): boolean {
    return !!this.financialAccounts?.find(
      (financialAccount: FinancialAccount) => financialAccount.financialGatewayToken.isInvestment
    );
  }

  get isFlinksBankingInstitution(): boolean {
    return !!this.financialAccounts?.find(
      (financialAccount: FinancialAccount) => financialAccount.financialGatewayToken.isBanking
    );
  }

  get hasAccountsWithSyncStatuses(): boolean {
    return !!this.financialAccounts?.find(
      (financialAccount: FinancialAccount) => financialAccount.hasSyncStatus
    );
  }

  get hasFlinksAccounts(): boolean {
    return !!this.financialAccounts?.find(
      (financialAccount: FinancialAccount) => financialAccount.isFlinksAccount
    );
  }

  get refreshingAccounts(): FinancialAccount[] | undefined {
    return this.financialAccounts?.filter(
      (financialAccount: FinancialAccount) => financialAccount.isRefreshingBalances
    );
  }

  get type(): string {
    return this.isFlinksBankingInstitution ? FlinksType.BANKING.key : FlinksType.INVESTMENT.key;
  }

  get connectedInstitutionFinancialAccounts(): FinancialAccount[] {
    if (!this.financialAccounts || !this.financialAccounts.length) {
      return [];
    }

    return this.financialAccounts.length > 1
      ? reject(
          this.financialAccounts,
          (financialAccount: FinancialAccount) => financialAccount.isFlinksPending
        )
      : this.financialAccounts;
  }
}
